import { useState, useEffect, useCallback, useRef } from "react";
import adminServices from "../../services/adminServices";
import Page from '../../components/Page';

const PAGE_SIZE_OPTIONS = [20, 50, 100, 250, 500, 1000];
const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const TABLE_COLUMNS = 8;
const EMPTY_COUNTS = { total: 0, freeOnly: 0, paidOnly: 0, bothFreePaid: 0 };
const CONTROL_HEIGHT = 38;

function initials(firstName, lastName, email) {
  const f = (firstName || "").trim();
  const l = (lastName || "").trim();
  if (f || l) return `${f[0] || ""}${l[0] || ""}`.toUpperCase();
  return (email || "?")[0].toUpperCase();
}

function fullName(firstName, lastName) {
  const name = `${firstName || ""} ${lastName || ""}`.trim();
  return name || "—";
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

// Returns "Paid", "Free" or "Paid + Free" based on the user's plans
function planType(plans) {
  const list = Array.isArray(plans) ? plans : [];
  if (list.length === 0) return "—";
  const hasPaid = list.some((p) => p.is_paid);
  const hasFree = list.some((p) => !p.is_paid);
  if (hasPaid && hasFree) return "Paid + Free";
  return hasPaid ? "Paid" : "Free";
}

function Checkbox({ checked, indeterminate, onChange, ariaLabel }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel}
      style={{ width: 16, height: 16, cursor: "pointer" }}
    />
  );
}

const inputStyle = {
  height: 34,
  padding: "0 10px",
  borderRadius: 8,
  border: "1px solid #E1DDD5",
  background: "#fff",
  fontSize: 13.5,
  color: "#22201B",
};

const outlineButtonStyle = (disabled) => ({
  height: CONTROL_HEIGHT,
  boxSizing: "border-box",
  padding: "0 14px",
  borderRadius: 10,
  border: "1px solid #E1DDD5",
  background: "#fff",
  fontSize: 13.5,
  fontWeight: 500,
  color: "#22201B",
  whiteSpace: "nowrap",
  cursor: disabled ? "default" : "pointer",
});

const planBadgeColors = {
  Paid: { background: "#E6F4EA", color: "#1E6B34" },
  Free: { background: "#EFECE6", color: "#6B6459" },
  "Paid + Free": { background: "#EFF4FF", color: "#1E3A8A" },
};

function StatCard({ label, value, hint, background, color, border }) {
  return (
    <div
      title={hint}
      style={{
        flex: "1 1 170px",
        minWidth: 160,
        padding: "12px 16px",
        borderRadius: 10,
        background,
        border: `1px solid ${border}`,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: "#6B6459" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

export default function AssessmentSubscribersList() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [records, setRecords] = useState([]);
  const [metadata, setMetadata] = useState({
    totalRecordCount: 0,
    currentPage: 1,
    perPage: DEFAULT_PAGE_SIZE,
    maxPage: 1,
    counts: EMPTY_COUNTS,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Review & send modal
  const [showPreview, setShowPreview] = useState(false);
  const [previewUsers, setPreviewUsers] = useState([]);
  const [previewQuery, setPreviewQuery] = useState("");

  const dateError =
    fromDate && toDate && fromDate > toDate
      ? '"From" date must be on or before "To" date.'
      : null;

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchUsers = useCallback(async () => {
    if (dateError) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await adminServices.getSubscribedUserList(
        page,
        pageSize,
        search || undefined,
        fromDate || undefined,
        toDate || undefined,
        statusFilter
      );
      const data = res.data || {};
      const meta = data.metadata || {
        totalRecordCount: 0,
        currentPage: 1,
        perPage: pageSize,
        maxPage: 1,
        counts: EMPTY_COUNTS,
      };
      setRecords(data.records || []);
      setMetadata(meta);

      if (meta.perPage && meta.perPage < pageSize) {
        setNotice({
          type: "error",
          text: `The server limits results to ${meta.perPage} per page, so ${pageSize} rows can't be shown at once.`,
        });
      }
    } catch (err) {
      setError(err.message || "Something went wrong while loading users.");
      setRecords([]);
      setMetadata({ totalRecordCount: 0, currentPage: 1, perPage: pageSize, maxPage: 1, counts: EMPTY_COUNTS });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, fromDate, toDate, statusFilter, dateError]);

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const res = await adminServices.getCrisisTemplate();
      const data = res.data || {};
      setTemplates(data);
    } catch (err) {
      setTemplatesError(err.message || "Couldn't load templates.");
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [records]);

  // Close the modal with Escape
  useEffect(() => {
    if (!showPreview) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !sending) {
        setShowPreview(false);
        setPreviewQuery("");
        setPreviewUsers([]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showPreview, sending]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (notice?.type !== "success") return undefined;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  const changePageSize = (n) => {
    setPage(1);
    setPageSize(n);
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setFromDate("");
    setToDate("");
    setStatusFilter("all");
    setPage(1);
  };

  const hasActiveFilters = Boolean(search || fromDate || toDate || statusFilter !== "all");

  const allIds = records.map((u) => u.id).filter((id) => id !== undefined && id !== null);
  const selectedInViewCount = allIds.filter((id) => selectedIds.has(id)).length;
  const allSelected = allIds.length > 0 && selectedInViewCount === allIds.length;
  const someSelected = selectedInViewCount > 0 && !allSelected;

  const toggleOne = (id) => {
    if (id === undefined || id === null) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allIds.forEach((id) => next.delete(id));
      } else {
        allIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const closePreview = () => {
    setShowPreview(false);
    setPreviewQuery("");
    setPreviewUsers([]);
  };

  const openPreview = () => {
    if (!selectedTemplateName) {
      setNotice({ type: "error", text: "Please select an email template." });
      return;
    }
    if (selectedIds.size === 0) {
      setNotice({ type: "error", text: "Please select at least one user." });
      return;
    }
    setNotice(null);
    setPreviewUsers(records.filter((r) => selectedIds.has(r.id)));
    setPreviewQuery("");
    setShowPreview(true);
  };

  const previewTemplateLabel =
    (Array.isArray(templates) &&
      templates.find((t) => t.mail_template_type === selectedTemplateName)?.title) ||
    selectedTemplateName;

  const previewFiltered = previewUsers.filter((u) => {
    if (!previewQuery.trim()) return true;
    const q = previewQuery.trim().toLowerCase();
    const name = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
    return name.includes(q) || (u.email || "").toLowerCase().includes(q);
  });
  const previewCheckedCount = previewFiltered.filter((u) => selectedIds.has(u.id)).length;
  const previewAllChecked = previewFiltered.length > 0 && previewCheckedCount === previewFiltered.length;
  const previewSomeChecked = previewCheckedCount > 0 && !previewAllChecked;
  const previewTotalChecked = previewUsers.filter((u) => selectedIds.has(u.id)).length;

  const togglePreviewAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      previewFiltered.forEach((u) => {
        if (previewAllChecked) next.delete(u.id);
        else next.add(u.id);
      });
      return next;
    });
  };

  const SendEmail = async () => {
    if (!selectedTemplateName) {
      setNotice({ type: "error", text: "Please select an email template." });
      return;
    }

    // Dedupe by email so nobody gets the email twice.
    const selectedEmails = Array.from(
      new Set(
        previewUsers
          .filter((u) => selectedIds.has(u.id))
          .map((u) => u.email)
          .filter(Boolean)
      )
    );

    if (selectedEmails.length === 0) {
      setNotice({ type: "error", text: "Please select at least one user with an email address." });
      return;
    }

    setSending(true);
    setNotice(null);
    try {
      await adminServices.sendCrisisEmail(selectedTemplateName, selectedEmails);
      const count = selectedEmails.length;
      setNotice({
        type: "success",
        text: `Email sent successfully to ${count} user${count === 1 ? "" : "s"}.`,
      });
      clearSelection();
      setSelectedTemplateName("");
      closePreview();
    } catch (err) {
      console.error("Error sending email:", err);
      setNotice({ type: "error", text: "Failed to send email. Please try again." });
    } finally {
      setSending(false);
    }
  };

  const maxPage = metadata.maxPage || 1;
  const total = metadata.totalRecordCount || 0;
  const counts = { ...EMPTY_COUNTS, ...(metadata.counts || {}), total };
  const canSend = !sending && !!selectedTemplateName && selectedIds.size > 0;
  const canGoPrev = !loading && page > 1;
  const canGoNext = !loading && page < maxPage;

  const selectedUserCount = new Set(
    records.filter((r) => selectedIds.has(r.id)).map((r) => r.email)
  ).size;

  return (
    <Page title="Assessment Subscribers">
      <div
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif',
          color: "#22201B",
          width: "100%",
          maxWidth: "none",
          boxSizing: "border-box",
          padding: 24,
        }}
      >
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>
            Assessment subscribers
          </h1>
          <p style={{ fontSize: 14, color: "#6B6459", margin: "4px 0 0" }}>
            {total} subscriber{total === 1 ? "" : "s"} (free &amp; paid)
            {hasActiveFilters ? " matching your filters" : ""}
          </p>
        </div>

        {/* Subscriber counts (respect the current search / date filters) */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
          <StatCard
            label="Total subscribers"
            value={counts.total}
            hint="All users with at least one assessment plan"
            background="#fff"
            border="#E1DDD5"
            color="#22201B"
          />
          <StatCard
            label="Free only"
            value={counts.freeOnly}
            hint="Users who have only free assessment plans"
            background="#FAF9F6"
            border="#E1DDD5"
            color="#6B6459"
          />
          <StatCard
            label="Paid only"
            value={counts.paidOnly}
            hint="Users who have only paid assessment plans"
            background="#F1F9F3"
            border="#CFE8D7"
            color="#1E6B34"
          />
          <StatCard
            label="Free + Paid (both)"
            value={counts.bothFreePaid}
            hint="Users who have both free and paid assessment plans"
            background="#EFF4FF"
            border="#DCE6FF"
            color="#1E3A8A"
          />
        </div>

        {/* Filters */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            marginBottom: 18,
            alignItems: "flex-end",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label htmlFor="subscribers-search" style={{ fontSize: 12, color: "#6B6459" }}>
              Search
              <input
                id="subscribers-search"
                type="text"
                placeholder="Name or email"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ ...inputStyle, minWidth: 220, display: "block", marginTop: 4 }}
              />
            </label>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label htmlFor="subscribers-from-date" style={{ fontSize: 12, color: "#6B6459" }}>
              Subscribed from
              <input
                id="subscribers-from-date"
                type="date"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
                style={{ ...inputStyle, display: "block", marginTop: 4 }}
              />
            </label>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label htmlFor="subscribers-to-date" style={{ fontSize: 12, color: "#6B6459" }}>
              Subscribed to
              <input
                id="subscribers-to-date"
                type="date"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
                style={{ ...inputStyle, display: "block", marginTop: 4 }}
              />
            </label>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label htmlFor="subscribers-status" style={{ fontSize: 12, color: "#6B6459" }}>
              Status
              <select
                id="subscribers-status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                style={{ ...inputStyle, display: "block", marginTop: 4, minWidth: 130, cursor: "pointer" }}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                height: 34,
                padding: "0 12px",
                borderRadius: 8,
                border: "1px solid #E1DDD5",
                background: "#fff",
                fontSize: 13.5,
                fontWeight: 500,
                cursor: "pointer",
                color: "#22201B",
              }}
            >
              Clear filters
            </button>
          )}

          <div style={{ flex: 1 }} />

          <button
            onClick={fetchUsers}
            disabled={loading}
            style={{
              height: 34,
              padding: "0 14px",
              borderRadius: 8,
              border: "1px solid #E1DDD5",
              background: "#fff",
              fontSize: 13.5,
              fontWeight: 500,
              cursor: loading ? "default" : "pointer",
              color: "#22201B",
            }}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {dateError && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "#FBEAEA",
              color: "#B3261E",
              fontSize: 13.5,
              marginBottom: 14,
            }}
          >
            {dateError}
          </div>
        )}

        {/* Success / error banner (hidden while the modal is open; errors show inside the modal) */}
        {notice && !showPreview && (
          <div
            role={notice.type === "error" ? "alert" : "status"}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 14px",
              borderRadius: 8,
              marginBottom: 14,
              fontSize: 13.5,
              fontWeight: 500,
              background: notice.type === "success" ? "#EAF6EE" : "#FBEAEA",
              color: notice.type === "success" ? "#1E7A3D" : "#B3261E",
              border: `1px solid ${notice.type === "success" ? "#CFE8D7" : "#F3CFCD"}`,
            }}
          >
            <span>{notice.text}</span>
            <button
              onClick={() => setNotice(null)}
              aria-label="Dismiss message"
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                fontSize: 18,
                lineHeight: 1,
                color: "inherit",
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        )}

        {selectedIds.size > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              padding: "12px 16px",
              borderRadius: 10,
              background: "#EFF4FF",
              border: "1px solid #DCE6FF",
              marginBottom: 16,
            }}
          >
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "#1E3A8A", whiteSpace: "nowrap" }}>
              {selectedIds.size} row{selectedIds.size === 1 ? "" : "s"} selected
              {selectedUserCount !== selectedIds.size
                ? ` (${selectedUserCount} unique user${selectedUserCount === 1 ? "" : "s"})`
                : ""}
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <select
                value={selectedTemplateName}
                onChange={(e) => setSelectedTemplateName(e.target.value)}
                disabled={templatesLoading || templates.length === 0 || sending}
                aria-label="Email template"
                style={{
                  height: 34,
                  padding: "0 10px",
                  borderRadius: 8,
                  border: "1px solid #C7D2FE",
                  background: "#fff",
                  fontSize: 13.5,
                  color: "#1E3A8A",
                  minWidth: 200,
                  cursor: templatesLoading || templates.length === 0 || sending ? "default" : "pointer",
                }}
              >
                <option value="" disabled>
                  Select template
                </option>

                {templatesLoading && (
                  <option value="">Loading templates…</option>
                )}

                {!templatesLoading && templates.length === 0 && (
                  <option value="">No templates available</option>
                )}

                {!templatesLoading &&
                  templates?.length > 0 &&
                  templates.map((t) => (
                    <option key={t.mail_template_type} value={t.mail_template_type}>
                      {t?.title}
                    </option>
                  ))}
              </select>

              <div style={{ width: 1, height: 20, background: "#C7D2FE" }} />

              <button
                onClick={clearSelection}
                disabled={sending}
                style={{
                  height: 34,
                  padding: "0 10px",
                  border: "none",
                  background: "none",
                  color: "#1E3A8A",
                  fontSize: 13.5,
                  fontWeight: 500,
                  cursor: sending ? "default" : "pointer",
                  opacity: sending ? 0.5 : 1,
                }}
              >
                Clear
              </button>

              <button
                onClick={openPreview}
                disabled={!canSend}
                style={{
                  height: 34,
                  padding: "0 18px",
                  borderRadius: 8,
                  border: "none",
                  background: canSend ? "#1E3A8A" : "#AEB9DC",
                  color: "#fff",
                  fontSize: 13.5,
                  fontWeight: 600,
                  cursor: canSend ? "pointer" : "default",
                  transition: "background 0.15s ease",
                }}
              >
                Review &amp; send
              </button>
            </div>
          </div>
        )}

        {templatesError && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "#FBEAEA",
              color: "#B3261E",
              fontSize: 13.5,
              marginBottom: 14,
            }}
          >
            Couldn't load templates: {templatesError}
          </div>
        )}

        {error && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "#FBEAEA",
              color: "#B3261E",
              fontSize: 13.5,
              marginBottom: 14,
            }}
          >
            Couldn't load users: {error}
          </div>
        )}

        <div style={{ border: "1px solid #E1DDD5", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#FAF9F6" }}>
                  <th
                    style={{
                      textAlign: "left",
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "#6B6459",
                      padding: "10px 16px",
                      borderBottom: "1px solid #E1DDD5",
                      width: 36,
                    }}
                  >
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleSelectAll}
                      ariaLabel="Select all rows on this page"
                    />
                  </th>
                  {["User", "Email", "Status", "Plan", "Last login", "Joined", "Subscribed"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: "#6B6459",
                        padding: "10px 16px",
                        borderBottom: "1px solid #E1DDD5",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renderTableBody({ loading, records, selectedIds, toggleOne })}
              </tbody>
            </table>
          </div>

          {/* Pagination footer: total count, rows per page, prev/next */}
          {total > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 16px",
                borderTop: "1px solid #E1DDD5",
                background: "#FAF9F6",
                fontSize: 13,
                color: "#6B6459",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <span>
                  Page {metadata.currentPage || page} of {maxPage} · {total} total
                </span>
                <label
                  htmlFor="subscribers-rows-per-page"
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  Rows per page
                  <select
                    id="subscribers-rows-per-page"
                    value={pageSize}
                    onChange={(e) => changePageSize(Number(e.target.value))}
                    disabled={loading}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: "1px solid #E1DDD5",
                      fontSize: 13,
                      color: "#22201B",
                      background: "#fff",
                      cursor: loading ? "default" : "pointer",
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {maxPage > 1 && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={!canGoPrev}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid #E1DDD5",
                      background: "#fff",
                      fontSize: 13,
                      cursor: canGoPrev ? "pointer" : "default",
                      opacity: canGoPrev ? 1 : 0.5,
                    }}
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                    disabled={!canGoNext}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid #E1DDD5",
                      background: "#fff",
                      fontSize: 13,
                      cursor: canGoNext ? "pointer" : "default",
                      opacity: canGoNext ? 1 : 0.5,
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Review before sending */}
        {showPreview && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(20,18,14,0.5)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="preview-title"
              style={{
                background: "#fff",
                borderRadius: 14,
                width: "100%",
                maxWidth: 760,
                maxHeight: "88vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px 20px",
                  borderBottom: "1px solid #E1DDD5",
                }}
              >
                <h2 id="preview-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  Review before sending
                </h2>
                <button
                  onClick={closePreview}
                  disabled={sending}
                  aria-label="Close preview"
                  style={{
                    border: "none",
                    background: "none",
                    fontSize: 24,
                    lineHeight: 1,
                    cursor: sending ? "default" : "pointer",
                    color: "#6B6459",
                  }}
                >
                  ×
                </button>
              </div>

              {/* Summary */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: "16px 20px 0" }}>
                <div
                  style={{
                    flex: "1 1 200px",
                    padding: "10px 14px",
                    borderRadius: 10,
                    background: "#EFF4FF",
                    border: "1px solid #DCE6FF",
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6B6459" }}>Learners selected</div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: "#1E3A8A" }}>
                    {previewTotalChecked}
                    <span style={{ fontSize: 13, fontWeight: 500, color: "#6B6459" }}>
                      {" "}of {previewUsers.length}
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    flex: "2 1 260px",
                    padding: "10px 14px",
                    borderRadius: 10,
                    background: "#FAF9F6",
                    border: "1px solid #E1DDD5",
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6B6459" }}>Email template</div>
                  <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4, wordBreak: "break-word" }}>
                    {previewTemplateLabel || "—"}
                  </div>
                </div>
              </div>

              {/* Search */}
              <div style={{ padding: "16px 20px 10px" }}>
                <input
                  type="text"
                  placeholder="Search selected learners by name or email"
                  aria-label="Search selected learners"
                  value={previewQuery}
                  onChange={(e) => setPreviewQuery(e.target.value)}
                  style={{
                    width: "100%",
                    height: CONTROL_HEIGHT,
                    boxSizing: "border-box",
                    padding: "0 12px",
                    borderRadius: 10,
                    border: "1px solid #E1DDD5",
                    fontSize: 13.5,
                  }}
                />
              </div>

              {/* List */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  margin: "0 20px",
                  border: "1px solid #E1DDD5",
                  borderRadius: 10,
                  minHeight: 120,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "8px 14px",
                    background: "#FAF9F6",
                    borderBottom: "1px solid #E1DDD5",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "#6B6459",
                    position: "sticky",
                    top: 0,
                  }}
                >
                  <Checkbox
                    checked={previewAllChecked}
                    indeterminate={previewSomeChecked}
                    onChange={togglePreviewAll}
                    ariaLabel="Select or unselect all listed learners"
                  />
                  <span>{previewFiltered.length} listed · uncheck to remove from this send</span>
                </div>

                {previewFiltered.length === 0 ? (
                  <div style={{ padding: 28, textAlign: "center", color: "#9C9184", fontSize: 14 }}>
                    No selected learners match your search.
                  </div>
                ) : (
                  previewFiltered.map((u, i) => {
                    const checked = selectedIds.has(u.id);
                    return (
                      <div
                        key={u.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "9px 14px",
                          borderBottom:
                            i === previewFiltered.length - 1 ? "none" : "1px solid #EFECE6",
                          opacity: checked ? 1 : 0.5,
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleOne(u.id)}
                          ariaLabel={`Include ${u.first_name || u.email}`}
                        />
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            background: "#EFECE6",
                            color: "#6B6459",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          {initials(u.first_name, u.last_name, u.email)}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>
                            {fullName(u.first_name, u.last_name)}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: "#6B6459",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {u.email || "—"}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Error inside modal */}
              {notice?.type === "error" && (
                <div
                  role="alert"
                  style={{
                    margin: "12px 20px 0",
                    padding: "10px 14px",
                    borderRadius: 8,
                    background: "#FBEAEA",
                    color: "#B3261E",
                    fontSize: 13.5,
                    fontWeight: 500,
                  }}
                >
                  {notice.text}
                </div>
              )}

              {/* Footer */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 20px" }}>
                <button onClick={closePreview} disabled={sending} style={outlineButtonStyle(sending)}>
                  Cancel
                </button>
                <button
                  onClick={SendEmail}
                  disabled={sending || previewTotalChecked === 0}
                  style={{
                    height: CONTROL_HEIGHT,
                    padding: "0 20px",
                    borderRadius: 10,
                    border: "none",
                    background: sending || previewTotalChecked === 0 ? "#AEB9DC" : "#1E3A8A",
                    color: "#fff",
                    fontSize: 13.5,
                    fontWeight: 600,
                    cursor: sending || previewTotalChecked === 0 ? "default" : "pointer",
                  }}
                >
                  {sending
                    ? "Sending…"
                    : `Send to ${previewTotalChecked} learner${previewTotalChecked === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Page>
  );
}

function renderTableBody({ loading, records, selectedIds, toggleOne }) {
  if (loading) {
    return (
      <tr>
        <td colSpan={TABLE_COLUMNS} style={{ padding: 32, textAlign: "center", color: "#9C9184", fontSize: 14 }}>
          Loading users…
        </td>
      </tr>
    );
  }

  if (records.length === 0) {
    return (
      <tr>
        <td colSpan={TABLE_COLUMNS} style={{ padding: 32, textAlign: "center", color: "#9C9184", fontSize: 14 }}>
          No matching assessment subscribers found.
        </td>
      </tr>
    );
  }

  return records.map((u, i) => {
    const isSelected = selectedIds.has(u.id);
    const type = planType(u.plans);
    const badge = planBadgeColors[type] || planBadgeColors.Free;

    return (
      <tr
        key={u.id}
        style={{
          background: isSelected ? "#F5F8FF" : "transparent",
          borderBottom: i === records.length - 1 ? "none" : "1px solid #EFECE6",
        }}
      >
        <td style={{ padding: "10px 16px" }}>
          <Checkbox
            checked={isSelected}
            onChange={() => toggleOne(u.id)}
            ariaLabel={`Select ${fullName(u.first_name, u.last_name)}`}
          />
        </td>
        <td style={{ padding: "10px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: "#EFECE6",
                color: "#6B6459",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {initials(u.first_name, u.last_name, u.email)}
            </div>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{fullName(u.first_name, u.last_name)}</span>
          </div>
        </td>
        <td style={{ padding: "10px 16px", fontSize: 14, color: "#413D36" }}>{u.email || "—"}</td>
        <td style={{ padding: "10px 16px", fontSize: 14, color: "#413D36" }}>{u.computed_status || "—"}</td>
        <td style={{ padding: "10px 16px" }}>
          <span
            style={{
              display: "inline-block",
              padding: "2px 10px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: badge.background,
              color: badge.color,
              whiteSpace: "nowrap",
            }}
          >
            {type}
          </span>
        </td>
        <td style={{ padding: "10px 16px", fontSize: 13, color: "#6B6459", whiteSpace: "nowrap" }}>
          {formatDate(u.lastLogin)}
        </td>
        <td style={{ padding: "10px 16px", fontSize: 13, color: "#6B6459", whiteSpace: "nowrap" }}>
          {formatDate(u.user_create_datetime)}
        </td>
        <td style={{ padding: "10px 16px", fontSize: 13, color: "#6B6459", whiteSpace: "nowrap" }}>
          {formatDate(u.subscribed_at)}
        </td>
      </tr>
    );
  });
}